import {Box, Button, Group, Input, Loader, Stack, Text, TextInput} from '@mantine/core'
import {IconArrowRight} from '@tabler/icons-react'
import {useState} from 'react'

import {getProjectBillingTermsSummaryFields} from '../jobs/billing-terms-display'
import {formatJobTime} from '../jobs/job-schedule-format'
import {ChangePreviewModal} from './change-preview-modal'
import {ScheduleBillingTermsModal} from './schedule-billing-terms-modal'
import {ScheduleBuildOutEditor} from './schedule-build-out-editor'
import type {ServiceScheduleEditorState} from './use-service-schedule-editor'

type ServiceScheduleEditorPanelProps = {
    editor: ServiceScheduleEditorState
    patternDisabled?: boolean
    cadenceDisabled?: boolean
    primaryActionLabel?: string
    onPrimaryAction?: () => void
    showPrimaryAction?: boolean
    footer?: React.ReactNode
}

export function ServiceScheduleEditorPanel({
    editor,
    patternDisabled = true,
    cadenceDisabled = true,
    primaryActionLabel = 'Review schedule changes',
    onPrimaryAction,
    showPrimaryAction = true,
    footer,
}: ServiceScheduleEditorPanelProps) {
    const [editingBillingTerms, setEditingBillingTerms] = useState(false)
    const {
        schedule,
        scheduling,
        setScheduling,
        scheduleBillingTerms,
        poNumber,
        setPoNumber,
        generatedJobs,
        changeSummary,
        isLoading,
        workerTypeNames,
        equipmentTypeNames,
        workerTypeOptions,
        equipmentTypeOptions,
        previewOpen,
        closePreview,
        previewData,
        previewChange,
        applyChange,
        handleReviewChanges,
        handleApplyChanges,
        handleToggleSkip,
    } = editor

    if (isLoading || scheduling == null) {
        return (
            <Group justify="center" py="xl">
                <Loader aria-label="Loading schedule editor" />
            </Group>
        )
    }

    return (
        <Stack gap="lg">
            <Group grow align="flex-start">
                <TextInput
                    label="PO number"
                    value={poNumber}
                    onChange={(event) => setPoNumber(event.currentTarget.value)}
                    description="Clear to explicitly use No PO."
                />
                {/* Versioning stays behind the scenes: editing stamps a new terms version rather
                    than offering a list of them. */}
                <Input.Wrapper label="Billing terms">
                    <Group gap="sm" wrap="nowrap" align="center" mt={4}>
                        <Text size="sm">
                            {scheduleBillingTerms == null
                                ? 'Unknown terms'
                                : getProjectBillingTermsSummaryFields(scheduleBillingTerms)
                                      .map((field) => `${field.label}: ${field.value}`)
                                      .join(' · ')}
                        </Text>
                        {schedule != null ? (
                            <Button
                                variant="subtle"
                                size="compact-sm"
                                onClick={() => setEditingBillingTerms(true)}
                                style={{flexShrink: 0}}
                            >
                                Edit
                            </Button>
                        ) : null}
                    </Group>
                </Input.Wrapper>
            </Group>
            <Text size="sm">
                <Text span fw={600}>
                    Service-visit time:
                </Text>{' '}
                {schedule?.serviceStartTime
                    ? formatJobTime(schedule.serviceStartTime)
                    : 'Unspecified'}
            </Text>
            <ScheduleBuildOutEditor
                scheduling={scheduling}
                onSchedulingChange={setScheduling}
                patternDisabled={patternDisabled}
                cadenceDisabled={cadenceDisabled}
                timeDisabled
                jobs={generatedJobs}
                workerTypeNames={workerTypeNames}
                equipmentTypeNames={equipmentTypeNames}
                workerTypeOptions={workerTypeOptions}
                equipmentTypeOptions={equipmentTypeOptions}
                onToggleSkip={
                    scheduling.pattern === 'deploy_service_pickup' ? handleToggleSkip : undefined
                }
            />

            {showPrimaryAction ? (
                <Box>
                    {footer}
                    <Group justify="flex-end" mt={footer ? 'md' : 0}>
                        <Button
                            color="blue"
                            onClick={onPrimaryAction ?? (() => void handleReviewChanges())}
                            loading={previewChange.isPending || applyChange.isPending}
                            rightSection={<IconArrowRight size={16} />}
                        >
                            {primaryActionLabel}
                        </Button>
                    </Group>
                </Box>
            ) : (
                footer
            )}

            {schedule != null ? (
                <ScheduleBillingTermsModal
                    opened={editingBillingTerms}
                    onClose={() => setEditingBillingTerms(false)}
                    scheduleId={schedule.id}
                    terms={scheduleBillingTerms}
                />
            ) : null}

            <ChangePreviewModal
                opened={previewOpen}
                title="Review schedule changes"
                preview={previewData}
                changes={changeSummary}
                loading={previewChange.isPending}
                applying={applyChange.isPending}
                onClose={closePreview}
                onApply={() => void handleApplyChanges()}
            />
        </Stack>
    )
}
